<template>
  <component
    :is="element"
    :aria-label="ariaLabel"
    :aria-describedby="ariaDescriptionId"
    class="tei-tiling-term"
    :class="{
      [`tei-tiling-term--${state}`]: !!state,
      [`tei-tiling-term--${format}`]: !!format,
      [`tei-tiling-term--${layout}`]: !!layout,
      'tei-tiling-term--disabled': disabled,
      'tei-tiling-term--floating': floating,
    }"
    @keydown="selectTerm"
    :tabindex="disabled || isInColumn ? -1 : 0"
  >
    <div
      class="tei-tiling-term__aria-description nebula-screenreader-only"
      :id="ariaDescriptionId"
      :v-text="ariaDescription"
    />
    <template v-if="isInColumn && !state && !disabled">
      <!-- This invisible matte provides a "path" for the user to access the remove button;
        otherwise the button would disappear as soon as the user moves their mouse toward it -->
      <div class="tei-tiling-term__remove-button-matte" />
      <button
        class="tei-tiling-term__remove-button"
        :aria-label="$t('remove item')"
        @click="$emit('remove-term')"
      >
        <NebulaIcon
          class="tei-tiling-term__remove-icon"
          size="s"
          symbol-id="trash"
        />
      </button>
    </template>
    <div
      v-if="format === 'images'"
      class="tei-tiling-term__image-wrapper"
    >
      <img
        class="tei-tiling-term__image"
        :alt="term.imageAlt"
        :src="term.imageUrl"
        :tabindex="isInColumn ? 0 : -1"
      >
      <div class="tei-tiling-term__image-overlay" />
      <K5ZoomButton
        class="tei-tiling-term__zoom-button"
        size="s"
        @click="openZoomModal"
      />
    </div>
    <div
      v-else
      class="tei-tiling-term__inner-wrap"
      :class="{
        'tei-tiling-term__inner-wrap--in-column': isInColumn,
      }"
      :aria-label="ariaLabel"
      :tabindex="isInColumn ? 0 : -1"
    >
      {{ text }}
    </div>
    <div
      v-if="state === 'correct'"
      class="tei-tiling-term__check-icon-wrapper
        tei-tiling-term__check-icon-wrapper--correct"
    >
      <CheckIconSmall class="tei-tiling-term__check-icon" />
    </div>
    <div
      v-else-if="state === 'incorrect'"
      class="tei-tiling-term__check-icon-wrapper
        tei-tiling-term__check-icon-wrapper--incorrect"
    >
      <XIconSmall class="tei-tiling-term__check-icon" />
    </div>

    <!-- This is the "ghost" that shows up when a term is about to be dropped -->
    <div class="tei-tiling-term__ghost">
      <svg
        class="tei-tiling-term__ghost-dashes"
        height="100%"
        width="100%"
        aria-hidden="true"
        focusable="false"
      >
        <rect
          height="100%"
          width="100%"
          fill="none"
          :rx="layout === 'flow' ? 20 : 12"
          :ry="layout === 'flow' ? 20 : 12"
          stroke="#fff"
          stroke-width="4"
          stroke-dasharray="8,8"
          stroke-dashoffset="0"
          stroke-linecap="butt"
        />
      </svg>
      <DropIcon class="tei-tiling-term__drop-icon" />
    </div>
  </component>
</template>

<script>
import { NebulaIcon } from '@discoveryedu/nebula-components';
import i18next from 'i18next'; // see note under `computed.ariaDescription`
import { uniqueId } from 'lodash-es';
import { mapActions } from 'pinia';
import K5ZoomButton from '@/components/ui-elements/K5ZoomButton.vue';
import CheckIconSmall from '@/components/ui-elements/texas-icons/CheckIconSmall.vue';
import DropIcon from '@/components/ui-elements/texas-icons/DropIcon.vue';
import XIconSmall from '@/components/ui-elements/texas-icons/XIconSmall.vue';
import { extractTextFromHtml } from '@/lib/utils';
import {
  useModalStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'TEITilingTerm',
  components: {
    CheckIconSmall,
    DropIcon,
    NebulaIcon,
    K5ZoomButton,
    XIconSmall,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    element: {
      type: String,
      default: 'li',
    },
    floating: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      default: 'text', // either 'images' or 'text'
    },
    isInColumn: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      required: true, // either 'tableWithColumn' or 'flow'
    },
    state: {
      type: String,
      default: null, // either 'correct' or 'incorrect'
    },
    term: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'arrow-key',
    'remove-term',
    'select-keyboard-term',
  ],
  data() {
    return {
      uniqueId: uniqueId(),
    };
  },
  computed: {
    ariaDescription() {
      // $t is not available in this file since the component is
      // technically inside a different Vue app than the "root" app. Here we're
      // passing the $t function in manually

      if (this.floating) {
        // eslint-disable-next-line max-len
        return i18next.t('Use the arrow keys to choose where to place this term, then press space to place it. Press escape to cancel.');
      }

      // eslint-disable-next-line max-len
      return i18next.t('Press the space key to select this term.');
    },
    ariaDescriptionId() {
      return `tei-tiling-term__aria-description--${this.uniqueId}`;
    },
    ariaLabel() {
      return extractTextFromHtml(this.term.accessibilityContent || this.term.text);
    },
    text() {
      return extractTextFromHtml(this.term.text);
    },
  },
  methods: {
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    openZoomModal() {
      this[types.OPEN_MODAL]({
        type: 'TEIImageZoomModal',
        imageUrl: this.term.imageUrl,
      });
    },
    selectTerm(e) {
      const allowedKeys = ['Left', 'ArrowLeft', 'Up', 'ArrowUp', 'Right', 'ArrowRight', 'Down', 'ArrowDown'];

      // Don't select a disabled term, or if we're trying to activate the zoom
      // or delete button
      if (this.disabled || e.target.closest('button')) return;

      if (e.key === ' ') {
        e.preventDefault();
        this.$emit('select-keyboard-term');
      } else if (allowedKeys.includes(e.key)) {
        this.$emit('arrow-key', e);
      }
    },
  },
};
</script>

<style lang="stylus">
@import './styles/tei-fonts';

.tei-tiling-term {
  tei-mikado-body-small-regular();

  align-items: stretch;
  color: $nebula-color-platform-neutral-900;
  display: flex;
  height: 55px;
  justify-content: center;
  line-height: $nebula-font-line-height-default;
  list-style-type: none;
  margin: 0;
  padding: 0;
  position: relative;
  transition: all $nebula-transition-default;
  user-select: none;
  z-index: 2;

  &:not(.tei-tiling-term--disabled):not(.tei-tiling-term--ghost):not(.tei-tiling-term--dragging) {
    cursor: move;

    &:hover, &:focus-visible, &:focus-within {
      transform: translateY(-10px);

      .tei-tiling-term__remove-button,
      .tei-tiling-term__remove-button-matte {
        opacity: 1;
      }

      .tei-tiling-term__image-wrapper
      .tei-tiling-term__inner-wrap {
        box-shadow: $nebula-shadow-400;
      }
    }
  }

  &--correct {
    .tei-tiling-term__inner-wrap {
      background-color: $nebula-color-feedback-success-100;
    }

    .tei-tiling-term__image-overlay {
      background-color: rgba($nebula-color-success, 0.5);
    }
  }

  &--incorrect,
  &--dragging {
    .tei-tiling-term__inner-wrap {
      background-color: $nebula-color-platform-neutral-300;
    }

    .tei-tiling-term__image-overlay {
      background-color: rgba($nebula-color-platform-neutral-900, 0.5);
    }
  }

  &--dragging {
    // We need lots of !important here to override inline styles
    // set by Sortable
    left: 0 !important;
    top: 0 !important;
    transform: translate(var(--dragged-term-x), var(--dragged-term-y)) !important;
    transition: none !important;

    &.tei-tiling-term--text {
      height: 55px !important;
    }
  }

  &--ghost {
    width: auto;
    z-index: 1;

    .tei-tiling-term__remove-button-matte,
    .tei-tiling-term__remove-button {
      opacity: 0;
    }

    .tiling-tei__response-column & {
      // Add specificity to prevent the &--images declaration from overriding this
      height: auto !important;
      width: auto !important;
    }
  }

  &--images {
    // This is 2:3 aspect ratio, but it has to use hard-coded values for Firefox
    height: 67px !important;
    justify-content: flex-start;
    width: 99px !important;
  }

  &--floating {
    // A transform is applied in TilingTEI.vue to position this term where
    // it should be.
    left: 0;
    position: absolute;
    top: 0;
  }

  &__remove-button {
    background-color: $nebula-color-platform-interface-800;
    border: 3px solid $nebula-color-white;
    border-radius: 50%;
    inset-inline-end: "calc(100% + %s)" % $nebula-space-half;
    opacity: 0;
    padding: $nebula-space-1x;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  &__remove-icon {
    display: block;
    fill: $nebula-color-white;
  }

  &__remove-button-matte {
    cursor: auto;
    height: 100%;
    inset-inline-start: - $nebula-space-3x;
    opacity: 0;
    position: absolute;
    width: $nebula-space-3x;
  }

  &__image-wrapper {
    background-color: $nebula-color-white;
    border: 2px solid $nebula-color-white;
    border-radius: $nebula-border-radius-default;
    box-shadow: $nebula-shadow-100;
    height: 67px;
    overflow: hidden;
    position: relative;
    transition: box-shadow $nebula-transition-default;
    width: 99px;
  }

  &__image {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
    &:focus,
    &:hover {
      border-radius: $nebula-border-radius-default;
    }
  }

  &__image-overlay {
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__zoom-button {
    inset-inline-end: 6px;
    position: absolute;
    top: 6px;
  }

  &__inner-wrap {
    align-items: center;
    background-color: $nebula-color-white;
    border: 3px solid $nebula-color-white;
    border-radius: $nebula-border-radius-large;
    box-shadow: $nebula-shadow-100;
    display: flex;
    justify-content: center;
    padding: 0 12px;
    transition: box-shadow $nebula-transition-default;
    width: 204px;

    &--in-column,
    .tei-tiling-term--floating & {
      min-width: 100%;
    }

    div {
      text-align: center; // align nested text inside rich-text terms
    }
  }

  &__check-icon-wrapper {
    border: 3px solid $nebula-color-white;
    border-radius: 50%;
    inset-inline-start: - $nebula-space-2x;
    padding: 0;
    position: absolute;
    top: -10px;

    &--correct {
      background-color: $nebula-color-feedback-success-800;
    }

    &--incorrect {
      background-color: $nebula-color-platform-neutral-900;
    }
  }

  &__check-icon {
    display: block;
    fill: $nebula-color-white;
    height: $nebula-space-4x;
    width: $nebula-space-4x;
  }

  &__ghost {
    background-color: var(--tei-column-drop-zone-color);
    border-radius: $nebula-border-radius-large;
    bottom: 0;
    display: none;
    left: 0;
    padding: $nebula-space-half;
    position: absolute;
    right: 0;
    top: 0;

    .tei-tiling-term--tableWithColumn & {
      border-radius: $nebula-border-radius-default;
    }
  }

  &__ghost-dashes {
    border-radius: $nebula-border-radius-large - $nebula-space-half;
    flex-grow: 1;

    .tei-tiling-term--tableWithColumn & {
      border-radius: $nebula-border-radius-default - $nebula-space-half;
    }
  }

  &__drop-icon {
    fill: $nebula-color-white;
    height: $nebula-space-3x;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: $nebula-space-3x;
  }
}
</style>
